/**
 * Qué puede indexar un buscador. Fuente única de verdad para:
 *  - `robots.txt` y `sitemap.xml`, generados en el build (`vite.config.ts`);
 *  - el `<meta name="robots">` que pinta `<NoIndex />` en runtime.
 *
 * 🔴 Por qué el `noindex` va en runtime y no en el robots: `condorsecurity.co` y
 * `app.condorsecurity.co` sirven EL MISMO `dist/` desde el mismo nginx, así que
 * hay un solo `robots.txt` para los dos hosts. No puede decir «el host de la app
 * entero no» sin decirlo también de la landing. Lo que sí distingue el host es el
 * código que corre en el navegador (`isOnAppHost()`), y Google ejecuta ese JS
 * antes de decidir si indexa.
 *
 * El robots igual bloquea las RUTAS de la app (/dashboard, /settings…), que es lo
 * que ahorra presupuesto de rastreo en los dos hosts.
 */

/** Rutas públicas que entran al sitemap. Las que tienen parámetro (trust, shared) no. */
export const RUTAS_PUBLICAS = ['/', '/free-tools'] as const;

/** Prefijos que no se rastrean en ningún host: la app autenticada y lo que es de un cliente. */
export const RUTAS_PRIVADAS = [
  '/dashboard',
  '/findings',
  // 🔴 `/reports` es prefijo de `/reports/shared/<token>`: un reporte compartido es PÚBLICO
  // (se abre sin sesión) pero es de un cliente, así que tampoco se indexa. El bloqueo lo
  // cubre a propósito, y la página además lleva `noindex` (<NoIndex /> en SharedReport).
  '/reports',
  '/schedules',
  '/projects',
  '/me/',
  '/settings',
  '/admin',
  '/integrations/',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/',
  '/access/',
] as const;

export function robotsTxt(landingOrigin: string): string {
  const origen = landingOrigin.replace(/\/$/, '');
  return [
    'User-agent: *',
    'Allow: /',
    ...RUTAS_PRIVADAS.map((p) => `Disallow: ${p}`),
    '',
    `Sitemap: ${origen}/sitemap.xml`,
    '',
  ].join('\n');
}

export function sitemapXml(landingOrigin: string, lastmod: string): string {
  const origen = landingOrigin.replace(/\/$/, '');
  const urls = RUTAS_PUBLICAS.map(
    (p) => `  <url>\n    <loc>${origen}${p}</loc>\n    <lastmod>${lastmod}</lastmod>\n  </url>`,
  ).join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}

/** Por qué esta URL no debe indexarse, o `null` si sí puede. */
export function motivoNoIndex(pathname: string, enHostDeApp: boolean, esRutaInexistente = false): string | null {
  if (esRutaInexistente) return 'ruta inexistente';
  if (enHostDeApp) return 'host de la app';
  if (RUTAS_PRIVADAS.some((p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`)))
    return 'ruta privada';
  return null;
}
